import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { findPost, POSTS } from "@/data/blog";
import { Sparkles, Calendar, Clock, ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const formatDate = (iso) => new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" });

// Minimal markdown renderer (h2, h3, lists, paragraphs, blockquote, bold, italic)
function renderMarkdown(md) {
  const lines = md.trim().split("\n");
  const out = [];
  let i = 0;
  let key = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith("## ")) {
      out.push(<h2 key={key++} className="font-display text-3xl font-bold mt-12 mb-5 tracking-tight">{inline(line.slice(3))}</h2>);
      i++;
    } else if (line.startsWith("### ")) {
      out.push(<h3 key={key++} className="font-display text-xl font-semibold mt-8 mb-3">{inline(line.slice(4))}</h3>);
      i++;
    } else if (line.startsWith("> ")) {
      out.push(<blockquote key={key++} className="border-l-4 border-primary pl-5 py-3 my-6 bg-primary/5 rounded-r-lg italic text-foreground/90">{inline(line.slice(2))}</blockquote>);
      i++;
    } else if (line.match(/^\d+\. /) || line.startsWith("- ") || line.startsWith("- [ ] ")) {
      const items = [];
      const ordered = !!line.match(/^\d+\. /);
      while (i < lines.length && (lines[i].match(/^\d+\. /) || lines[i].startsWith("- "))) {
        const stripped = lines[i].replace(/^\d+\. /, "").replace(/^- (\[ \] )?/, "");
        items.push(<li key={key++} className="leading-relaxed">{inline(stripped)}</li>);
        i++;
      }
      out.push(ordered
        ? <ol key={key++} className="list-decimal list-outside space-y-2 my-5 pl-6 text-foreground/85">{items}</ol>
        : <ul key={key++} className="list-disc list-outside space-y-2 my-5 pl-6 text-foreground/85">{items}</ul>
      );
    } else if (line.trim() === "") {
      i++;
    } else {
      out.push(<p key={key++} className="leading-relaxed my-4 text-foreground/85">{inline(line)}</p>);
      i++;
    }
  }
  return out;
}

function inline(text) {
  // bold + italic + code (very small subset)
  const parts = [];
  let remaining = text;
  let k = 0;
  while (remaining.length > 0) {
    const m = remaining.match(/(\*\*([^*]+)\*\*|\*([^*]+)\*|`([^`]+)`)/);
    if (!m) { parts.push(remaining); break; }
    if (m.index > 0) parts.push(remaining.slice(0, m.index));
    if (m[2]) parts.push(<strong key={k++} className="text-foreground font-semibold">{m[2]}</strong>);
    else if (m[3]) parts.push(<em key={k++} className="text-primary">{m[3]}</em>);
    else if (m[4]) parts.push(<code key={k++} className="font-mono text-xs px-1.5 py-0.5 rounded bg-secondary/60 border border-border text-primary">{m[4]}</code>);
    remaining = remaining.slice(m.index + m[0].length);
  }
  return parts;
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = findPost(slug);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!post) return <Navigate to="/blog" replace />;

  const others = POSTS.filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />

      <header className="relative z-10 border-b border-border/50 bg-[#050B14]/60 backdrop-blur-xl sticky top-0">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-primary flex items-center justify-center"><Sparkles className="w-4 h-4 text-white" /></div>
            <span className="font-display font-bold text-lg tracking-tight">SmartStockAI</span>
          </Link>
          <Link to="/blog"><Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-2" />Voltar ao blog</Button></Link>
        </div>
      </header>

      <article className="relative z-10 max-w-3xl mx-auto px-6 pt-16 pb-20">
        <div className="flex items-center gap-3 text-xs text-muted-foreground mb-5">
          <span className="px-2 py-0.5 rounded-md bg-primary/15 text-primary border border-primary/30">{post.category}</span>
          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDate(post.date)}</span>
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {post.readMin} min de leitura</span>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight">{post.title}</h1>
        <p className="text-lg text-muted-foreground mt-5 leading-relaxed">{post.description}</p>

        <div className="mt-12 prose prose-invert max-w-none">
          {renderMarkdown(post.body)}
        </div>

        <div className="mt-16 p-6 rounded-xl border border-primary/30 bg-gradient-to-br from-primary/10 to-transparent">
          <h3 className="font-display text-xl font-semibold">Aplique isso na sua loja em 30 segundos.</h3>
          <p className="text-sm text-muted-foreground mt-2">Crie sua conta gratuita com dados demo realistas. Sem cartão.</p>
          <Link to="/signup">
            <Button className="mt-4 bg-primary hover:bg-primary/90">Começar grátis <ArrowRight className="w-4 h-4 ml-2" /></Button>
          </Link>
        </div>
      </article>

      {others.length > 0 && (
        <section className="relative z-10 max-w-3xl mx-auto px-6 py-12 border-t border-border/50">
          <h3 className="font-display text-lg font-semibold mb-5">Continue lendo</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {others.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="p-5 rounded-xl border border-border bg-card hover:border-primary/40 transition">
                <div className="text-xs text-primary mb-2">{p.category}</div>
                <div className="font-display font-semibold">{p.title}</div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <footer className="relative z-10 border-t border-border/50 py-8 text-center text-sm text-muted-foreground">
        © 2026 SmartStockAI · Inteligência preditiva para o varejo
      </footer>
    </div>
  );
}
